import { FIRST_PAGE_INDEX } from 'constants/common';
import { INITIAL_CLOSET_MEMBER_FILTER_VALUES } from 'constants/member/connect';
import { memberListActions } from 'features/member/reducer/memberListSlice';
import keyboardKey from 'keyboard-key';
import { addQueryString, getNewValueForQuery, getParsedQuery } from 'modules/utils/queryString';
import { ChangeEvent, useEffect, useState, KeyboardEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CheckObjectValue } from 'types/common';
import { UserStatus } from 'types/member';

const useClosetMemberSearchFilter = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [keyword, setKeyword] = useState<string>('');
  const [userStatus, setUserStatus] = useState<CheckObjectValue>(
    INITIAL_CLOSET_MEMBER_FILTER_VALUES.userStatus,
  );
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(FIRST_PAGE_INDEX);

  const onChangeUserStatus = (values: UserStatus[]): void => {
    setUserStatus({
      [UserStatus.ACTIVE]: values.includes(UserStatus.ACTIVE),
      [UserStatus.INACTIVE]: values.includes(UserStatus.INACTIVE),
    });
  };

  const onChangeKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  const onChangePage = (pageIndex: number) => {
    addQueryString({ history, objValue: { page: pageIndex } });
    setCurrentPageIndex(pageIndex);
    dispatch(
      memberListActions.getMemberList({
        keyword,
        page: pageIndex,
        userStatus,
      }),
    );
  };

  const getSearchResult = () => {
    addQueryString({
      history,
      objValue: {
        keyword,
        userStatus: getNewValueForQuery(userStatus),
        page: FIRST_PAGE_INDEX,
      },
    });
  };

  const onKeyDownKeyword = (e: KeyboardEvent<HTMLInputElement>) => {
    const inputKey = keyboardKey.getCode(e);
    if (inputKey === keyboardKey.Enter) {
      getSearchResult();
    }
  };

  const resetFilter = () => {
    addQueryString({ history, reset: true });
    setKeyword('');
    setCurrentPageIndex(FIRST_PAGE_INDEX);
    setUserStatus(INITIAL_CLOSET_MEMBER_FILTER_VALUES.userStatus);

    dispatch(
      memberListActions.getMemberList({
        keyword: '',
        page: FIRST_PAGE_INDEX,
        userStatus: INITIAL_CLOSET_MEMBER_FILTER_VALUES.userStatus,
      }),
    );
  };

  useEffect(() => {
    // 새로고침시 url query 읽어와서 세팅
    let keyword = '';
    let page = FIRST_PAGE_INDEX;
    let userStatus = INITIAL_CLOSET_MEMBER_FILTER_VALUES.userStatus;

    const queries = history.location.search;

    if (queries.length) {
      const parsedQuery = getParsedQuery(queries);
      keyword = parsedQuery['keyword']?.[0] ?? '';
      page = +(parsedQuery['page']?.[0] ?? FIRST_PAGE_INDEX);

      const userStatusFromQuery = parsedQuery['userStatus'] ?? [
        String(UserStatus.ACTIVE),
        String(UserStatus.INACTIVE),
      ];
      userStatus = {
        [UserStatus.ACTIVE]: userStatusFromQuery.includes(String(UserStatus.ACTIVE)),
        [UserStatus.INACTIVE]: userStatusFromQuery.includes(String(UserStatus.INACTIVE)),
      };
    }

    setKeyword(keyword);
    setCurrentPageIndex(page);
    setUserStatus(userStatus);
    dispatch(
      memberListActions.getMemberList({
        keyword,
        page,
        userStatus,
      }),
    );
  }, [history.location.search]);

  return {
    currentPageIndex,
    keyword,
    userStatus,
    onChangeKeyword,
    onKeyDownKeyword,
    onChangeUserStatus,
    getSearchResult,
    onChangePage,
    resetFilter,
  };
};

export default useClosetMemberSearchFilter;
